import { FC, useCallback, useMemo, useState } from 'react';
import { MdBolt, MdCheck, MdClose, MdEdit } from 'react-icons/md';
import { fmtVal } from '../utils/debugPanelUtils';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTypedSelector } from '@hooks/useTypedSelector';
import type { IField } from '@models/IFormData';
import type { DirectWriteHandler, IDirectWriteTarget } from '../FormInspector/directWrite.types';
import { directWriteKey, parseInspectorValue } from '../FormInspector/formInspectorUtils';
import DirectWriteConfirm from '../FormInspector/DirectWriteConfirm';
import { directWriteDisplay } from '../FormInspector/directWriteReason';
import { describeDirectWriteFailure, useConfirmedDirectWrite } from '../FormInspector/useConfirmedDirectWrite';

type WriteMode = 'form' | 'direct';

interface IRequisiteEditorProps {
    /** Полный ключ реквизита (pipe-путь `|Document|X`) — он же fieldName для прямой записи. */
    fieldKey: string;
    /** docId документа, в который идёт прямая запись (для строк линков — их собственный docId). */
    docId: string;
    /** RHF-путь `fields.[N].value` для записи в форму; null — записи в форму нет. */
    rhfPath: string | null;
    datatype?: string;
    value: IField['value'];
    /** Форма в режиме редактирования — доступна запись в форму. */
    isEditMode: boolean;
    /** Прямая запись доступна (валидный docId вне нового документа). */
    directAvailable: boolean;
    onDirectWrite: DirectWriteHandler;
    /** Запись идёт в другой документ (строка таблицы-линка) — показать адрес чужого docId. */
    foreign?: boolean;
    refreshParentId?: string;
    compact?: boolean;
}

const toDraft = (value: IField['value']): string => {
    if (value === null || value === undefined) return '';
    if (value === true) return '1';
    if (value === false) return '0';
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return JSON.stringify(value);
    return String(value);
};

/**
 * Редактор одного реквизита в жучке: значение + выбор пути записи (в форму / прямая) + запись.
 * Один и тот же компонент обслуживает основной реквизит и каждый связанный.
 */
const RequisiteValueEditor: FC<IRequisiteEditorProps> = ({
    fieldKey,
    docId,
    rhfPath,
    datatype,
    value,
    isEditMode,
    directAvailable,
    onDirectWrite,
    foreign,
    refreshParentId,
    compact,
}) => {
    const form = useFormContext();
    const formAvailable = isEditMode && !!rhfPath;
    const [mode, setMode] = useState<WriteMode>(() => (formAvailable ? 'form' : 'direct'));
    const currentDraft = toDraft(value);
    const [previousValue, setPreviousValue] = useState(currentDraft);
    const [draft, setDraft] = useState(currentDraft);
    const [flash, setFlash] = useState<string | null>(null);
    // «Было» прямой записи — сохранённое значение документа, а не несохранённая правка формы
    const storedField = useTypedSelector((state) =>
        !foreign && state.document.data?.id === docId
            ? state.document.data.fields?.find((field) => field.name === fieldKey)
            : undefined,
    );
    const storedValue = storedField ? storedField.value : value;
    if (previousValue !== currentDraft) {
        setPreviousValue(currentDraft);
        // Обновляем нетронутый ввод, сохраняя ещё не применённую ручную правку.
        if (draft === previousValue) setDraft(currentDraft);
    }
    const activeMode = mode === 'form' && formAvailable || !directAvailable ? 'form' : 'direct';

    const base = datatype?.split('(')[0];
    const isBoolean = base === 'boolean' || (!datatype && typeof value === 'boolean');
    const isLongtext = base === 'longtext';
    const isNumber = base === 'integer' || base === 'real' || base === 'money';

    const target = useMemo<IDirectWriteTarget>(() => ({
        key: directWriteKey(docId, fieldKey),
        docId,
        fieldName: fieldKey,
        foreign,
        refreshParentId,
    }), [docId, fieldKey, foreign, refreshParentId]);

    const parsedDraft = useCallback(() => parseInspectorValue(draft, datatype), [draft, datatype]);

    const writeDirectValue = useCallback(
        (nextValue: IField['value'], force: boolean, reason?: string) => onDirectWrite(target, nextValue, force, reason),
        [onDirectWrite, target],
    );
    const showWritten = useCallback(() => setFlash(foreign ? `Записано в ${docId}` : 'Записано'), [foreign, docId]);
    const {
        writing,
        failure,
        pending,
        request: requestDirect,
        confirm: confirmDirect,
        reset: resetPending,
        fail,
        dismissFailure,
    } = useConfirmedDirectWrite(writeDirectValue, showWritten);
    const failureView = failure && describeDirectWriteFailure(failure);

    const writeToForm = useCallback(() => {
        if (!formAvailable || !rhfPath) return;
        try {
            form.setValue(rhfPath as never, parsedDraft() as never, { shouldDirty: true, shouldValidate: true });
        } catch (error) {
            fail(error instanceof Error ? error.message : String(error));
            return;
        }
        dismissFailure();
        setFlash('Применено к форме');
    }, [dismissFailure, fail, form, formAvailable, rhfPath, parsedDraft]);

    const writeDirect = useCallback((force = false) => {
        if (!directAvailable) return;
        let nextValue: IField['value'];
        try {
            nextValue = parsedDraft();
        } catch (error) {
            fail(error instanceof Error ? error.message : String(error));
            return;
        }
        requestDirect(nextValue, force);
    }, [directAvailable, fail, parsedDraft, requestDirect]);

    const submit = useCallback(() => {
        if (activeMode === 'form') writeToForm();
        else writeDirect();
    }, [activeMode, writeToForm, writeDirect]);

    const changeDraft = (next: string) => {
        setDraft(next);
        setFlash(null);
        resetPending();
    };

    const switchMode = (next: WriteMode) => {
        setMode(next);
        dismissFailure();
        resetPending();
    };

    const bothModes = formAvailable && directAvailable;
    const primaryLabel = activeMode === 'form'
        ? 'Применить к форме'
        : foreign
            ? `Записать в ${docId}`
            : 'Записать в документ';

    return (
        <div className={`rf-field-editor rf-field-editor--${activeMode} ${compact ? 'rf-field-editor--compact' : ''}`}>
            <div className="rf-field-editor__heading">
                <span className="rf-field-formula-tooltip__section-title">{compact ? 'Значение реквизита' : 'Значение поля'}</span>
                <span className="rf-field-editor__hint" title={fmtVal(value)}>Сейчас: {fmtVal(value)}</span>
            </div>
            <div className="rf-field-editor__value">
                {isBoolean ? (
                    <select
                        className="rf-field-editor__input rf-field-editor__input--select"
                        value={draft}
                        onChange={(e) => changeDraft(e.target.value)}
                        aria-label={`Новое значение ${fieldKey}`}
                    >
                        <option value="">null</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                    </select>
                ) : isLongtext ? (
                    <textarea
                        className="rf-field-editor__input rf-field-editor__input--textarea"
                        value={draft}
                        onChange={(e) => changeDraft(e.target.value)}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') setDraft(toDraft(value)); }}
                        rows={3}
                        aria-label={`Новое значение ${fieldKey}`}
                    />
                ) : (
                    <input
                        className="rf-field-editor__input"
                        type={isNumber ? 'number' : 'text'}
                        step={base === 'integer' ? '1' : 'any'}
                        value={draft}
                        onChange={(e) => changeDraft(e.target.value)}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') submit(); if (e.key === 'Escape') setDraft(toDraft(value)); }}
                        aria-label={`Новое значение ${fieldKey}`}
                    />
                )}
                <button
                    type="button"
                    className="rf-field-editor__apply-btn"
                    onClick={submit}
                    disabled={writing || (activeMode === 'form' ? !formAvailable : !directAvailable)}
                >
                    {writing ? 'Запись…' : primaryLabel}
                </button>
            </div>

            <div className="rf-field-editor__footer">
                {bothModes && (
                    <div className="rf-field-editor__seg" role="group" aria-label="Путь записи">
                        <button
                            type="button"
                            className={`rf-field-editor__seg-btn ${activeMode === 'form' ? 'is-active' : ''}`}
                            aria-pressed={activeMode === 'form'}
                            onClick={() => switchMode('form')}
                        >
                            <MdEdit size={14} />В форму
                        </button>
                        <button
                            type="button"
                            className={`rf-field-editor__seg-btn ${activeMode === 'direct' ? 'is-active' : ''}`}
                            aria-pressed={activeMode === 'direct'}
                            onClick={() => switchMode('direct')}
                        >
                            <MdBolt size={14} />В документ
                        </button>
                    </div>
                )}

                <div className="rf-field-editor__note" title={activeMode === 'form' ? 'Сохранится вместе с карточкой документа.' : `Сохранится сразу в документ ${docId}, только это поле.`}>
                    {activeMode === 'form'
                        ? 'При сохранении карточки'
                        : <>Сразу в документ <b>{docId}</b>{foreign && <span> · связанный</span>}</>}
                </div>
            </div>

            {pending && activeMode === 'direct' && (
                <DirectWriteConfirm
                    docId={docId}
                    fieldName={fieldKey}
                    before={directWriteDisplay(storedValue)}
                    after={directWriteDisplay(pending.value)}
                    writing={writing}
                    onConfirm={confirmDirect}
                    onCancel={resetPending}
                />
            )}
            {flash && !failure && (
                <div className="rf-field-editor__flash" role="status"><MdCheck size={12} /> {flash}</div>
            )}
            {failureView && (
                <div className="rf-field-editor__error" role="alert">
                    <b>{failureView.title}</b>
                    <span>{failureView.detail}</span>
                    {failureView.canForce && (
                        <button type="button" onClick={() => writeDirect(true)}>Записать всё равно</button>
                    )}
                    <button type="button" aria-label="Скрыть ошибку" onClick={dismissFailure}><MdClose size={11} /></button>
                </div>
            )}
        </div>
    );
};

const FormRequisiteEditor: FC<IRequisiteEditorProps & { rhfPath: string }> = (props) => {
    const form = useFormContext();
    // Контролы используют и fields.[N].value, и fields.N.value. Подписка на массив
    // видит оба варианта, включая reset; сам редактор смонтирован только в открытом жучке.
    useWatch({ control: form.control, name: props.rhfPath.split(/[.[]/)[0] });
    const value = form.getValues(props.rhfPath) as IField['value'];
    return <RequisiteValueEditor {...props} value={value} />;
};

const RequisiteEditor: FC<IRequisiteEditorProps> = (props) => {
    const form = useFormContext();
    return form && props.isEditMode && props.rhfPath && !props.foreign
        ? <FormRequisiteEditor {...props} rhfPath={props.rhfPath} />
        : <RequisiteValueEditor {...props} />;
};

export default RequisiteEditor;
